import { c as _c } from "react/compiler-runtime";
// Third Party
import * as React from 'react';
import Image from 'next/image';

// Utils
import { useQuery } from '@apollo/client/react';
import { PlaceTypeFilter, PlacesAutocompleteDocument, RegionsListAllFeaturedDocument } from '@gql';
import { useDebounce } from '@hooks';
import { GRAY_PIXEL_DATA_URL } from '@utils/constants';
import { imagekitLoader } from '@utils/imagekit-loader';
import { concatenateAndReplace } from '@utils/test-helpers';

// UI
import { Icon, IconNames } from '@components/icon';
import boothbayHarborMe from '../../../public/assets/img/featured-searches/boothbay-harbor-me.png';
import capeMayNJ from '../../../public/assets/img/featured-searches/cape-may-nj.png';
import hawaii from '../../../public/assets/img/featured-searches/hawaii.png';
import austin from '../../../public/assets/img/featured-searches/austin-tx.png';
import breck from '../../../public/assets/img/featured-searches/breck-co.png';
import { Button } from '@components/button';
import { IPlacesSelectAutocompleteOption } from '@components/places-select-autocomplete';
import { Typography } from '@components/typography';
import { ButtonIcon } from '@components/button-icon';
import { extractFirstPhraseBeforeComma } from '@utils/extract-first-phrase-before-comma';

// Suggestions shown if there are no featured
const suggestions = [{
  name: 'Austin, TX',
  imageUrl: austin,
  placeId: 'ChIJLwPMoJm1RIYRetVp1EtGm10'
}, {
  name: 'Breckenridge, CO',
  imageUrl: breck,
  placeId: 'ChIJwecmbD32aocReqKAZn'
}, {
  name: 'Boothbay Harbor, ME',
  imageUrl: boothbayHarborMe,
  placeId: 'ChIJ69Mkld6YrUwREWQ8hGp28MM'
}, {
  name: 'Cape May, NJ',
  imageUrl: capeMayNJ,
  placeId: 'ChIJlffuOkRUv4kRovRz4Zz3t8I'
}, {
  name: 'Hawaii',
  imageUrl: hawaii,
  placeId: 'ChIJBeB5Twbb_3sRKIbMdNKCd0s'
}];
const imageSizePx = 16 * 4;
type Props = {
  onChange?: (name: string, value: IPlacesSelectAutocompleteOption | null) => void;
  onClear: VoidFunction;
  initialValue?: IPlacesSelectAutocompleteOption | null;
  placeName: string;
};
export const MobileSelectAutocomplete: React.FC<Props> = t0 => {
  const $ = _c(44);
  const {
    onChange,
    onClear,
    initialValue: t1,
    placeName
  } = t0;
  const initialValue = t1 === undefined ? null : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      variables: {
        page: 1,
        pageSize: 20
      }
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    data: destinations,
    loading
  } = useQuery(RegionsListAllFeaturedDocument, t2);
  const [searchInput, setSearchInput] = React.useState("");
  const [value, setValue] = React.useState(initialValue);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [options, setOptions] = React.useState(t3);
  const inputRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const debouncedSearchValue = useDebounce(searchInput, 300);
  const t4 = debouncedSearchValue || "";
  let t5;
  if ($[2] !== t4) {
    t5 = {
      variables: {
        query: t4,
        type: PlaceTypeFilter.Regions
      }
    };
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    data
  } = useQuery(PlacesAutocompleteDocument, t5);
  let t6;
  if ($[4] !== data) {
    t6 = () => {
      if (data?.placesAutocomplete) {
        setOptions(data.placesAutocomplete.map(_temp));
      }
    };
    $[4] = data;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== data || $[7] !== value) {
    t7 = [data, value];
    $[6] = data;
    $[7] = value;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  React.useEffect(t6, t7);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = event => {
      const inputValue = event.target.value;
      if (containerRef.current) {
        containerRef.current.scrollLeft = 0;
      }
      setSearchInput(inputValue);
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handleChange = t8;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t9;
  if ($[10] !== destinations || $[11] !== loading || $[12] !== onChange || $[13] !== onClear || $[14] !== options || $[15] !== placeName) {
    const onSelect = (name, value_0) => {
      setValue(value_0);
      onChange?.(name, value_0);
      if (inputRef.current && value_0 && value_0.label) {
        inputRef.current.value = value_0.label;
      }
    };
    let t15;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = () => {
        if (inputRef.current) {
          inputRef.current.value = "";
        }
      };
      $[22] = t15;
    } else {
      t15 = $[22];
    }
    const clearInput = t15;
    const searchResultsPresent = options.length > 0;
    t13 = "w-full h-full bg-white text-gray-dark";
    let t16;
    if ($[23] !== placeName) {
      t16 = placeName !== "" ? extractFirstPhraseBeforeComma(placeName) : "Where to?";
      $[23] = placeName;
      $[24] = t16;
    } else {
      t16 = $[24];
    }
    let t17;
    if ($[25] !== t16) {
      t17 = <input autoFocus={true} ref={inputRef} type="text" onChange={handleChange} placeholder={t16} className="pl-4 p-2 focus:outline-none w-full" data-testid="@mobile/search-input" />;
      $[25] = t16;
      $[26] = t17;
    } else {
      t17 = $[26];
    }
    let t18;
    if ($[27] !== onClear || $[28] !== placeName) {
      t18 = placeName && placeName !== "" && <ButtonIcon variant="text" color="gray" size="sm" icon={IconNames.CIRCLE_X} iconSize="lg" ariaLabel="Clear search input" onClick={() => {
        onClear();
        clearInput();
        setOptions([]);
      }} className="ps-0" data-testid="@mobile/clear-search-input" />;
      $[27] = onClear;
      $[28] = placeName;
      $[29] = t18;
    } else {
      t18 = $[29];
    }
    if ($[30] !== t17 || $[31] !== t18) {
      t14 = <div className="flex flex-row items-center p-2 mx-2 border-2 border-primary-main rounded-full">{t17}{t18}</div>;
      $[30] = t17;
      $[31] = t18;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    t9 = "flex flex-col pt-4";
    const t19 = searchResultsPresent ? "Search Results" : "Popular Getaways";
    if ($[33] !== t19) {
      t10 = <div className="px-4 pb-2"><Typography variant="subtitle2" component="p">{t19}</Typography></div>;
      $[33] = t19;
      $[34] = t10;
    } else {
      t10 = $[34];
    }
    t11 = searchResultsPresent ? options && options.slice(0, 5).map(option => <div key={option.value} className="flex flex-row w-full overflow-hidden"><SearchResult key={option.value} name={option.label} imageElement={<div className="bg-gray-xlight w-full h-full flex flex-col justify-center items-center"><Icon name={IconNames.LOCATION_DOT} solid={true} className="w-4 h-4" /></div>} imageHeight={4} imageWidth={4} onSelect={() => onSelect(option.label, option)} /></div>) : <div ref={containerRef} className="flex flex-row overflow-x-auto">{destinations && destinations?.regionsListAllFeatured.entries.map(destination => <div key={destination.name} className="flex-shrink-0 px-2"><PopularGetaway key={destination.name} name={destination.name} imageElement={<div className="relative"><Image width={100} height={40} loader={imagekitLoader} className="aspect-[1] object-cover object-center" src={destination.imageUrl || ""} alt={destination.name || "search result image"} placeholder="blur" blurDataURL={GRAY_PIXEL_DATA_URL} /></div>} onSelect={() => onSelect(destination.name, {
          label: destination.name,
          value: destination.placeId
        })} />{" "}</div>)}</div>;
    t12 = !searchResultsPresent && !loading && !destinations && suggestions.slice(0, 5).map(suggestion => <SearchResult key={suggestion.name} name={suggestion.name} imageElement={<div className="relative"><Image width={imageSizePx} height={imageSizePx} className="object-center object-cover" src={suggestion.imageUrl} alt={suggestion.name || "search result image"} placeholder="blur" blurDataURL={GRAY_PIXEL_DATA_URL} /></div>} imageHeight={12} imageWidth={12} onSelect={() => onSelect(suggestion.name, {
      label: suggestion.name,
      value: suggestion.placeId
    })} />);
    $[10] = destinations;
    $[11] = loading;
    $[12] = onChange;
    $[13] = onClear;
    $[14] = options;
    $[15] = placeName;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
    $[20] = t14;
    $[21] = t9;
  } else {
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t13 = $[19];
    t14 = $[20];
    t9 = $[21];
  }
  let t15;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== t9) {
    t15 = <div className={t9}>{t10}{t11}{t12}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t9;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t13 || $[41] !== t14 || $[42] !== t15) {
    t16 = <div className={t13}>{t14}{t15}</div>;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
};
const PopularGetaway: React.FC<{
  name: string;
  imageElement: JSX.Element;
  onSelect?: VoidFunction;
}> = t0 => {
  const $ = _c(11);
  const {
    name,
    imageElement,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== imageElement) {
    t1 = imageElement && <div className="w-min"><div className="w-16 h-16 rounded-lg flex overflow-hidden">{imageElement}</div></div>;
    $[0] = imageElement;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== name) {
    t2 = concatenateAndReplace(name);
    $[2] = name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `@search/${t2}`;
  let t4;
  if ($[4] !== name || $[5] !== t3) {
    t4 = <span className="text-primary-main text-left font-semibold text-sm" data-testid={t3}>{name}</span>;
    $[4] = name;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onSelect || $[8] !== t1 || $[9] !== t4) {
    t5 = <Button onClick={onSelect} className="flex flex-col align-items-start hover:bg-gray-xlight pb-2 pt-3 justify-start space-y-2" color="gray" variant="text">{t1}{t4}</Button>;
    $[7] = onSelect;
    $[8] = t1;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
const sizeClasses: Record<number, string> = {
  4: 'w-4 h-4',
  12: 'w-12 h-12',
  16: 'w-16 h-16'
};
const SearchResult: React.FC<{
  name: string;
  imageElement: JSX.Element;
  imageHeight: number;
  imageWidth: number;
  onSelect?: VoidFunction;
}> = t0 => {
  const $ = _c(12);
  const {
    name,
    imageElement,
    imageWidth,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== imageElement || $[1] !== imageWidth) {
    t1 = imageElement && <div className="w-min"><div className={`${sizeClasses[imageWidth] || "w-12 h-12"} rounded-lg flex overflow-hidden`}>{imageElement}</div></div>;
    $[0] = imageElement;
    $[1] = imageWidth;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== name) {
    t2 = concatenateAndReplace(name);
    $[3] = name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = `@search/${t2}`;
  let t4;
  if ($[5] !== name || $[6] !== t3) {
    t4 = <span className="flex overflow-hidden ml-4 text-primary-main text-left font-semibold text-sm" data-testid={t3}>{name}</span>;
    $[5] = name;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onSelect || $[9] !== t1 || $[10] !== t4) {
    t5 = <Button onClick={onSelect} className="flex flex-row w-full align-items-start hover:bg-gray-xlight justify-start" color="gray" variant="text">{t1}{t4}</Button>;
    $[8] = onSelect;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
function _temp(place) {
  return {
    label: place.name,
    value: place.id
  };
}